/**
 * Distinct enemies that several INDEPENDENT projectiles of one cast reach.
 *
 * Every bolt flies its own corridor and stops after `cap = maxPierce ?? 1`
 * enemies, taken in the order its swept disc touches them (`attack-instance.ts`
 * `sweptCircleToi`: along-path position minus the half-chord of the dilated
 * disc, so an enemy on the axis is touched before one at the edge of the same
 * depth). An enemy the bolt starts inside (the disc of the swept radius around
 * its start, the half behind it included) is touched at time 0, and those tie
 * on the enemy id. Ids carry no position, so that tie is one random order, the
 * same for every bolt: each enemy has a uniform rank `u`, and two enemies both
 * inside the start discs of two bolts stand in the same order in both. An
 * enemy two bolts both reach is one enemy to an event that counts
 * once per target (`composite-execution.ts`, `per: TARGET`), so the area union
 * (every enemy in the union) over-counts a capped bolt: it takes only the
 * first `cap` enemies of its corridor, and a bolt blocked by a nearer enemy
 * never reaches the one behind it.
 *
 * The footprints are fixed; only the enemies are random (the cast field,
 * `cast-field.ts`: the aimed enemy `T` at distance `D`, the others a Poisson
 * process of intensity `g` outside the empty disc of radius `D`). `B_i(p)` is
 * the part of corridor `i` the bolt touches before it touches `p`. By Mecke's
 * formula an enemy at `p` meets the others as an independent Poisson field, so
 *
 * ```text
 * E[distinct] = P(T is hit) + integral g(p) P(an enemy at p is hit) dp
 * hit at p    = some bolt i containing p has fewer than cap_i enemies in B_i(p)
 * cap_i       = cap, less one where T itself is in B_i(p)
 * ```
 *
 * The counts of the bolts are sums of independent Poisson counts over the
 * atoms of the overlap (which of the `B_i(p)` hold a point), so the event is
 * exact: inclusion-exclusion over the bolts that hold one enemy
 * (`cap_i = 1`, the common case), a count table otherwise. A single bolt
 * reduces to `unitExpectedHits` (`E[min(cap, count)]`), which the tests pin.
 *
 * The integral is a deterministic lattice of cells (`latticeOf`): a square a
 * corridor edge crosses is split into the parts the same corridors hold, each
 * with its share of the area, so what a corridor holds does not depend on where
 * its edges fall. The enemies of a cell's own distance, itself included, are
 * half before it (the midpoint rule), those of a start disc by id. Only the cells
 * two corridors share pay for the atoms, and the cell side grows until they are
 * at most `SHARED_CELL_BUDGET`. The price is within 1 % of the same corridors on
 * cells of 0.04 m (`test/monte-carlo/projectile-union-lattice.test.ts`); where the
 * budget would need cells that hold more than `CELL_MASS_BOUND` enemies, the
 * price is refused (`ProjectileUnionTooLarge`).
 */
import type { CastFieldEvidence } from '../context.js';
import { castDistances, footprintContains, othersDensityScale, othersDensityShape, type Footprint, type FieldRegion } from './cast-field.js';

export type Corridor = Extract<Footprint, { readonly kind: 'CORRIDOR' }>;

/** Most corridors one cast may hold; the overlap atoms grow as 2^bolts. */
const MAX_BOLTS = 12;
/** Cells shared by two corridors the exact atoms are summed over, per cell pair. */
const SHARED_CELL_BUDGET = 2500;
/** Cells the lattice may hold, and the finest cell side it starts from. */
const MAX_LATTICE_CELLS = 40000;
const FINEST_CELL_M = 0.05;
/** The lattice is coarsened at most this many times to fit `SHARED_CELL_BUDGET`. */
const FIT_ATTEMPTS = 12;
/**
 * The most enemies a lattice cell may hold in expectation (at the densest part of the cast field). The
 * error of the integral grows with the mass of a cell; the corridors checked against fine cells
 * hold at most 0.005 per cell, and a union whose budget would need heavier cells than this is refused.
 */
const CELL_MASS_BOUND = 0.02;
/** Contact times closer than this (metres of path) are one distance: the lattice cannot tell them apart. */
const KEY_TOLERANCE_M = 1e-9;
/** Largest count table (states x atoms x count levels) a pierce above one may build per cell. */
const COUNT_TABLE_BUDGET = 6_000_000;

export const PROJECTILE_UNION_TOO_LARGE = 'COMPOSITE_PROJECTILE_UNION_TOO_LARGE';

export interface ProjectileUnionRefusal { readonly code: string; readonly message: string }

/** Thrown by `distinctProjectileHits` where no lattice within the budget bounds its error; the caller turns it into a refusal. */
export class ProjectileUnionTooLarge extends Error {
  constructor(readonly refusal: ProjectileUnionRefusal) {
    super(refusal.message);
  }
}

/** `undefined` when the exact distinct-enemy count of these bolts can be computed. */
export function projectileUnionRefusal(bolts: number, cap: number): ProjectileUnionRefusal | undefined {
  if (bolts > MAX_BOLTS) {
    return { code: PROJECTILE_UNION_TOO_LARGE, message: `${bolts} projectile corridors overlap in more ways than the exact count enumerates (at most ${MAX_BOLTS})` };
  }
  if (cap > 1 && (cap + 1) ** bolts * 2 ** bolts * (cap + 1) > COUNT_TABLE_BUDGET) {
    return { code: PROJECTILE_UNION_TOO_LARGE, message: `${bolts} projectile corridors that each pierce ${cap} enemies overlap in more ways than the exact count enumerates` };
  }
  return undefined;
}

/** Nodes and weights of an 8-point Gauss-Legendre rule on [0, 1]: the integral over an enemy's id rank. */
const RANK_NODES: readonly { readonly u: number; readonly weight: number }[] = [
  [0.1834346424956498, 0.3626837833783620], [0.5255324099163290, 0.3137066458778873],
  [0.7966664774136267, 0.2223810344533745], [0.9602898564975363, 0.1012285362903763],
].flatMap(([x, w]) => [{ u: (1 - x!) / 2, weight: w! / 2 }, { u: (1 + x!) / 2, weight: w! / 2 }]);

/** Where an enemy at `(x, y)` inside corridor `c` stands in `c`'s contact order (`sweptCircleToi`, scaled to metres): negative inside the spawn disc. */
function touchKey(c: Corridor, x: number, y: number): number {
  const dx = x - c.x;
  const dy = y - c.y;
  const along = dx * Math.cos(c.facingRad) + dy * Math.sin(c.facingRad);
  const lateral = dx * Math.sin(c.facingRad) - dy * Math.cos(c.facingRad);
  return along - Math.sqrt(Math.max(0, c.halfWidthM * c.halfWidthM - lateral * lateral));
}

interface Geometry {
  readonly corridors: readonly Corridor[];
  readonly cells: Lattice;
  /** Bit `i` set when the cell lies in corridor `i`. */
  readonly masks: Uint16Array;
  /** Bit `i` set when the cell lies in the disc corridor `i` starts inside: everything there is touched at time 0. */
  readonly spawn: Uint16Array;
  /** `touchKey` of corridor `i` at cell `c`, floored at 0 (the spawn disc ties), at `i * cells + c`. */
  readonly keys: Float64Array;
  /** Per corridor, its cells in the order the bolt touches them, and the keys in that order. */
  readonly sorted: readonly { readonly cells: Int32Array; readonly keys: Float64Array }[];
  /** Per corridor, the number of its cells touched before cell `c` (`rankLow`) and before or together with it (`rankHigh`), at `i * cells + c`. */
  readonly rankLow: Int32Array;
  readonly rankHigh: Int32Array;
  /** Cells held by two or more corridors. */
  readonly shared: Int32Array;
  /** For each cell, its index in `shared`, or -1. */
  readonly sharedIndex: Int32Array;
  /** For shared cell `a` and shared cell `b`: the corridors `j` of `a` whose `B_j(a)` holds `b`, at `a * shared + b`. */
  readonly before: Uint16Array;
  /** The same for the corridors that touch `a` and `b` together (the cells the lattice puts at one distance): half of `b` is before `a`. */
  readonly tied: Uint16Array;
}

const lowerBound = (keys: Float64Array, key: number): number => {
  let lo = 0;
  let hi = keys.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (keys[mid]! < key) lo = mid + 1; else hi = mid;
  }
  return lo;
};

const popcount = (v: number): number => {
  let n = 0;
  for (let x = v; x !== 0; x &= x - 1) n += 1;
  return n;
};

/**
 * Where a bolt on `c` reaches: the corridor, and the half of the disc it starts inside that lies behind its start. An
 * enemy there is touched at time 0 and takes one of the bolt's enemies as any other in the spawn disc does, so the
 * bolts of a fan released on one enemy all start inside the same disc. The far end of the swept disc, which the price's
 * footprint also leaves out, is not modelled (the same convention as a lone bolt's price).
 */
function reaches(c: Corridor, x: number, y: number): boolean {
  if (footprintContains(c, x, y)) return true;
  const dx = x - c.x;
  const dy = y - c.y;
  return dx * Math.cos(c.facingRad) + dy * Math.sin(c.facingRad) < 0 && dx * dx + dy * dy <= c.halfWidthM * c.halfWidthM;
}

/** The box of the corridors and the discs their bolts start inside. */
function reachBounds(corridors: readonly Corridor[]): FieldRegion['bounds'] {
  let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
  for (const c of corridors) {
    const endX = c.x + c.lengthM * Math.cos(c.facingRad);
    const endY = c.y + c.lengthM * Math.sin(c.facingRad);
    minX = Math.min(minX, c.x - c.halfWidthM, endX - c.halfWidthM);
    maxX = Math.max(maxX, c.x + c.halfWidthM, endX + c.halfWidthM);
    minY = Math.min(minY, c.y - c.halfWidthM, endY - c.halfWidthM);
    maxY = Math.max(maxY, c.y + c.halfWidthM, endY + c.halfWidthM);
  }
  return { minX, maxX, minY, maxY };
}

/** Sub-samples per cell side that tell which corridors hold which part of a cell. */
const SUBSAMPLES = 4;

/**
 * A cell is a piece of one lattice square that the same corridors hold: a square an edge of a
 * corridor crosses is several cells, each at the centre of its sub-samples with its share of the
 * area. The area each corridor holds is then right to a fraction of a square, not to a whole
 * column of them, however the edges fall against the lattice.
 */
interface Lattice { readonly xs: Float64Array; readonly ys: Float64Array; readonly areasM2: Float64Array; readonly masks: Uint16Array }

function latticeOf(corridors: readonly Corridor[], bounds: FieldRegion['bounds'], step: number): Lattice {
  const { minX, maxX, minY, maxY } = bounds;
  const nx = Math.max(1, Math.ceil((maxX - minX) / step));
  const ny = Math.max(1, Math.ceil((maxY - minY) / step));
  const dx = (maxX - minX) / nx;
  const dy = (maxY - minY) / ny;
  const xs: number[] = [];
  const ys: number[] = [];
  const areas: number[] = [];
  const masks: number[] = [];
  const parts = new Map<number, { n: number; x: number; y: number }>();
  for (let i = 0; i < nx; i += 1) {
    for (let j = 0; j < ny; j += 1) {
      parts.clear();
      for (let a = 0; a < SUBSAMPLES; a += 1) {
        for (let b = 0; b < SUBSAMPLES; b += 1) {
          const x = minX + (i + (a + 0.5) / SUBSAMPLES) * dx;
          const y = minY + (j + (b + 0.5) / SUBSAMPLES) * dy;
          let mask = 0;
          for (let k = 0; k < corridors.length; k += 1) if (reaches(corridors[k]!, x, y)) mask |= 1 << k;
          if (mask === 0) continue;
          const part = parts.get(mask) ?? { n: 0, x: 0, y: 0 };
          part.n += 1; part.x += x; part.y += y;
          parts.set(mask, part);
        }
      }
      for (const [mask, part] of parts) {
        xs.push(part.x / part.n); ys.push(part.y / part.n);
        areas.push((dx * dy * part.n) / (SUBSAMPLES * SUBSAMPLES));
        masks.push(mask);
      }
    }
  }
  return { xs: Float64Array.from(xs), ys: Float64Array.from(ys), areasM2: Float64Array.from(areas), masks: Uint16Array.from(masks) };
}

function buildGeometry(corridors: readonly Corridor[], maxStepM: number, fixedStepM?: number): Geometry {
  const bounds = reachBounds(corridors);
  let step = fixedStepM ?? Math.max(FINEST_CELL_M, Math.sqrt(((bounds.maxX - bounds.minX) * (bounds.maxY - bounds.minY)) / MAX_LATTICE_CELLS));
  let cells: Lattice;
  for (let attempt = 0; ; attempt += 1) {
    cells = latticeOf(corridors, bounds, step);
    if (fixedStepM !== undefined) break;
    if (step > maxStepM) {
      throw new ProjectileUnionTooLarge({ code: PROJECTILE_UNION_TOO_LARGE, message: `${corridors.length} projectile corridors need cells of ${step.toFixed(2)} m to keep their overlap within ${SHARED_CELL_BUDGET} cells, and the error is bounded only to ${maxStepM.toFixed(2)} m` });
    }
    let shared = 0;
    for (let c = 0; c < cells.xs.length; c += 1) if (popcount(cells.masks[c]!) >= 2) shared += 1;
    if (shared <= SHARED_CELL_BUDGET) break;
    if (attempt === FIT_ATTEMPTS) {
      throw new ProjectileUnionTooLarge({ code: PROJECTILE_UNION_TOO_LARGE, message: `${corridors.length} projectile corridors overlap in more than ${SHARED_CELL_BUDGET} cells at the coarsest lattice that bounds the error` });
    }
    step *= Math.max(1.05, Math.sqrt(shared / SHARED_CELL_BUDGET));
  }
  const count = cells.xs.length;
  const masks = cells.masks;
  const keys = new Float64Array(corridors.length * count).fill(Number.NaN);
  const spawn = new Uint16Array(count);
  const sorted: { cells: Int32Array; keys: Float64Array }[] = [];
  const rankLow = new Int32Array(corridors.length * count).fill(-1);
  const rankHigh = new Int32Array(corridors.length * count).fill(-1);
  corridors.forEach((corridor, i) => {
    const members: number[] = [];
    for (let c = 0; c < count; c += 1) {
      if ((masks[c]! & (1 << i)) === 0) continue;
      const key = touchKey(corridor, cells.xs[c]!, cells.ys[c]!);
      if (key <= 0) spawn[c] = spawn[c]! | (1 << i);
      keys[i * count + c] = Math.max(0, key);
      members.push(c);
    }
    members.sort((a, b) => keys[i * count + a]! - keys[i * count + b]! || a - b);
    const ordered = Int32Array.from(members);
    const orderedKeys = Float64Array.from(members, (c) => keys[i * count + c]!);
    for (const c of members) {
      rankLow[i * count + c] = lowerBound(orderedKeys, keys[i * count + c]! - KEY_TOLERANCE_M);
      rankHigh[i * count + c] = lowerBound(orderedKeys, keys[i * count + c]! + KEY_TOLERANCE_M);
    }
    sorted.push({ cells: ordered, keys: orderedKeys });
  });

  const sharedCells = Int32Array.from({ length: count }, (_unused, c) => c).filter((c) => popcount(masks[c]!) >= 2);
  const before = new Uint16Array(sharedCells.length * sharedCells.length);
  const tied = new Uint16Array(sharedCells.length * sharedCells.length);
  for (let a = 0; a < sharedCells.length; a += 1) {
    const ca = sharedCells[a]!;
    for (let b = 0; b < sharedCells.length; b += 1) {
      const cb = sharedCells[b]!;
      let bits = 0;
      let together = 0;
      for (let j = 0; j < corridors.length; j += 1) {
        const bit = 1 << j;
        // Two cells of one spawn disc are touched together at time 0 and go by the ids: not a tie of the lattice.
        if ((masks[ca]! & bit) === 0 || (masks[cb]! & bit) === 0 || (spawn[ca]! & spawn[cb]! & bit) !== 0) continue;
        const gap = keys[j * count + ca]! - keys[j * count + cb]!;
        if (gap > KEY_TOLERANCE_M) bits |= bit;
        else if (gap >= -KEY_TOLERANCE_M) together |= bit;
      }
      before[a * sharedCells.length + b] = bits;
      tied[a * sharedCells.length + b] = together;
    }
  }
  const sharedIndex = new Int32Array(count).fill(-1);
  sharedCells.forEach((c, a) => { sharedIndex[c] = a; });
  return { corridors, cells, masks, spawn, keys, sorted, rankLow, rankHigh, shared: sharedCells, sharedIndex, before, tied };
}

const MAX_GEOMETRIES = 8;
const geometries = new Map<string, Geometry>();

function geometryOf(corridors: readonly Corridor[], maxStepM: number, fixedStepM?: number): Geometry {
  const key = `${maxStepM}|${fixedStepM}|${JSON.stringify(corridors)}`;
  const known = geometries.get(key);
  if (known !== undefined) return known;
  const built = buildGeometry(corridors, maxStepM, fixedStepM);
  if (geometries.size >= MAX_GEOMETRIES) geometries.clear();
  geometries.set(key, built);
  return built;
}

/** P(Poisson(mean) < limit). */
function poissonBelow(mean: number, limit: number): number {
  let term = Math.exp(-mean);
  let sum = 0;
  for (let k = 0; k < limit; k += 1) {
    sum += term;
    term *= mean / (k + 1);
  }
  return sum;
}

/** P(at least one of the corridors in `alive` is still open), each holding fewer than `limit[i]` enemies, with the atoms' means given. */
function openProbability(alive: number, limit: Int32Array, atoms: Float64Array, bolts: number): number {
  let holdsNone = true;
  for (let i = 0; i < bolts; i += 1) if ((alive & (1 << i)) !== 0 && limit[i] !== 1) holdsNone = false;
  if (holdsNone) {
    // Every open corridor holds none: P(union) by inclusion-exclusion over the corridors, on the mass each subset reaches.
    const z = Float64Array.from(atoms);
    for (let i = 0; i < bolts; i += 1) {
      const bit = 1 << i;
      if ((alive & bit) === 0) continue;
      for (let u = alive; ; u = (u - 1) & alive) {
        if ((u & bit) !== 0) z[u] = z[u]! + z[u ^ bit]!;
        if (u === 0) break;
      }
    }
    const total = z[alive]!;
    let open = 0;
    for (let s = alive; s !== 0; s = (s - 1) & alive) {
      open += (popcount(s) % 2 === 1 ? 1 : -1) * Math.exp(-(total - z[alive & ~s]!));
    }
    return open;
  }

  const members: number[] = [];
  for (let i = 0; i < bolts; i += 1) if ((alive & (1 << i)) !== 0) members.push(i);
  const radix = members.map((i) => limit[i]! + 1);
  const states = radix.reduce((product, r) => product * r, 1);
  const top = Math.max(...members.map((i) => limit[i]!));
  let dist = new Float64Array(states);
  dist[0] = 1;
  for (let pattern = alive; pattern !== 0; pattern = (pattern - 1) & alive) {
    const mean = atoms[pattern]!;
    if (!(mean > 0)) continue;
    const step = new Float64Array(top + 1);
    let term = Math.exp(-mean);
    let used = 0;
    for (let k = 0; k < top; k += 1) {
      step[k] = term;
      used += term;
      term *= mean / (k + 1);
    }
    step[top] = Math.max(0, 1 - used);
    const next = new Float64Array(states);
    for (let s = 0; s < states; s += 1) {
      const mass = dist[s]!;
      if (mass === 0) continue;
      let rest = s;
      const digits: number[] = [];
      for (const r of radix) { digits.push(rest % r); rest = Math.floor(rest / r); }
      for (let k = 0; k <= top; k += 1) {
        let target = 0;
        let place = 1;
        members.forEach((i, m) => {
          const d = (pattern & (1 << i)) === 0 ? digits[m]! : Math.min(limit[i]!, digits[m]! + k);
          target += d * place;
          place *= radix[m]!;
        });
        next[target] = next[target]! + mass * step[k]!;
      }
    }
    dist = next;
  }
  let open = 0;
  for (let s = 0; s < states; s += 1) {
    let rest = s;
    let isOpen = false;
    for (let m = 0; m < members.length; m += 1) {
      if (rest % radix[m]! < limit[members[m]!]!) isOpen = true;
      rest = Math.floor(rest / radix[m]!);
    }
    if (isOpen) open += dist[s]!;
  }
  return open;
}

export interface DistinctHits {
  /** Σ over the distance law of P(`T` stands in some corridor). */
  readonly primaryShare: number;
  /** Distinct enemies reached, `T` standing at the aimed distance. */
  readonly distinct: number;
  /** The same when nobody is aimed at: the corridors hold only what stands in them. */
  readonly distinctWithoutPrimary: number;
}

interface NodeCounts { readonly inside: boolean; readonly distinct: number; readonly distinctWithoutPrimary: number }

function nodeCounts(geo: Geometry, frame: FieldRegion['frame'], distanceM: number, cap: number, field: CastFieldEvidence, density: number): NodeCounts {
  const { corridors, cells, masks, spawn, keys, sorted, rankLow, rankHigh, shared, sharedIndex, before, tied } = geo;
  const bolts = corridors.length;
  const count = cells.xs.length;
  const scale = othersDensityScale(field, density);
  const mass = new Float64Array(count);
  for (let c = 0; c < count; c += 1) mass[c] = othersDensityShape(cells.xs[c]!, cells.ys[c]!, frame, distanceM, field) * scale * cells.areasM2[c]!;
  const prefix = sorted.map((s) => {
    const sums = new Float64Array(s.cells.length + 1);
    for (let k = 0; k < s.cells.length; k += 1) sums[k + 1] = sums[k]! + mass[s.cells[k]!]!;
    return sums;
  });
  const sharedMass = Float64Array.from(shared, (c) => mass[c]!);
  // What stands in each bolt's spawn disc.
  const spawnMass = new Float64Array(bolts);
  for (let c = 0; c < count; c += 1) {
    for (let i = 0; i < bolts; i += 1) if ((spawn[c]! & (1 << i)) !== 0) spawnMass[i] = spawnMass[i]! + mass[c]!;
  }

  const tx = 0;
  const ty = frame === 'TARGET' ? 0 : distanceM;
  const tIn = corridors.map((corridor) => reaches(corridor, tx, ty));
  const tRaw = corridors.map((corridor, i) => (tIn[i]! ? touchKey(corridor, tx, ty) : Number.NaN));
  const tKey = tRaw.map((key) => Math.max(0, key));
  let tSpawn = 0;
  tRaw.forEach((key, i) => { if (key <= 0) tSpawn |= 1 << i; });
  const inside = tIn.some(Boolean);

  const atoms = new Float64Array(1 << bolts);
  const limit = new Int32Array(bolts);
  const meanBefore = new Float64Array(bolts);
  const sharedSpawn = Uint16Array.from(shared, (c) => spawn[c]!);

  // The mass of the shared cells by the corridors whose B holds it, for one point: `table[pattern]` over `listed`.
  // `by` is the row of that point (the corridors whose B holds a shared cell by position), `together` the corridors that touch
  // the cell at its own distance (half of it is before the point), `spawnBits` the corridors in whose spawn disc the point stands
  // (a cell there is before it when it ranks below `u`).
  const table = new Float64Array(1 << bolts);
  const listed: number[] = [];
  const gather = (u: number, spawnBits: number, base: number, by: Uint16Array, together: Uint16Array | undefined): void => {
    for (const pattern of listed) table[pattern] = 0;
    listed.length = 0;
    const put = (pattern: number, m: number): void => {
      if ((pattern & (pattern - 1)) === 0) return;
      if (table[pattern] === 0) listed.push(pattern);
      table[pattern] = table[pattern]! + m;
    };
    for (let b = 0; b < shared.length; b += 1) {
      const m = sharedMass[b]!;
      if (!(m > 0)) continue;
      const row = by[base + b]!;
      const tie = together === undefined ? 0 : together[base + b]!;
      const byId = spawnBits & sharedSpawn[b]!;
      if ((tie | byId) === 0) { put(row, m); continue; }
      for (let g = 0; g < (tie === 0 ? 1 : 2); g += 1) {
        for (let r = 0; r < (byId === 0 ? 1 : 2); r += 1) {
          let pattern = row;
          let part = m;
          if (tie !== 0) { if (g === 0) pattern |= tie; part *= 0.5; }
          if (byId !== 0) { if (r === 0) { pattern |= byId; part *= u; } else part *= 1 - u; }
          put(pattern, part);
        }
      }
    }
  };

  // P(an enemy standing at the gathered point is hit) by the corridors in `alive`, with `limit` and `meanBefore` set for them.
  const hitAt = (alive: number): number => {
    if (alive === 0) return 0;
    if ((alive & (alive - 1)) === 0) {
      const i = 31 - Math.clz32(alive);
      return poissonBelow(meanBefore[i]!, limit[i]!);
    }
    const touched: number[] = [];
    for (const pattern of listed) {
      const held = pattern & alive;
      if ((held & (held - 1)) === 0) continue;
      if (atoms[held] === 0) touched.push(held);
      atoms[held] = atoms[held]! + table[pattern]!;
    }
    for (let i = 0; i < bolts; i += 1) {
      const bit = 1 << i;
      if ((alive & bit) === 0) continue;
      let overlap = 0;
      for (const pattern of touched) if ((pattern & bit) !== 0) overlap += atoms[pattern]!;
      atoms[bit] = Math.max(0, meanBefore[i]! - overlap);
      touched.push(bit);
    }
    const open = openProbability(alive, limit, atoms, bolts);
    for (const pattern of touched) atoms[pattern] = 0;
    return open;
  };

  // The aimed enemy ranks `u` among the enemies of the spawn discs it stands in.
  let hitTarget = 0;
  if (inside) {
    let targetAlive = 0;
    for (let i = 0; i < bolts; i += 1) if (tIn[i]) { targetAlive |= 1 << i; limit[i] = cap; }
    const targetRow = new Uint16Array(shared.length);
    for (let b = 0; b < shared.length; b += 1) {
      let bits = 0;
      for (let j = 0; j < bolts; j += 1) {
        if ((targetAlive & (1 << j)) !== 0 && (masks[shared[b]!]! & (1 << j)) !== 0 && keys[j * count + shared[b]!]! < tKey[j]!) bits |= 1 << j;
      }
      targetRow[b] = bits;
    }
    const at = (u: number): number => {
      for (let i = 0; i < bolts; i += 1) {
        if ((targetAlive & (1 << i)) === 0) continue;
        meanBefore[i] = (tSpawn & (1 << i)) !== 0 ? u * spawnMass[i]! : prefix[i]![lowerBound(sorted[i]!.keys, tKey[i]!)]!;
      }
      gather(u, tSpawn, 0, targetRow, undefined);
      return hitAt(targetAlive);
    };
    hitTarget = tSpawn === 0 ? at(0.5) : RANK_NODES.reduce((total, node) => total + node.weight * at(node.u), 0);
  }

  let withTarget = 0;
  let without = 0;
  for (let c = 0; c < count; c += 1) {
    const m = mass[c]!;
    if (!(m > 0)) continue;
    const mask = masks[c]!;
    const cellSpawn = spawn[c]!;
    const a = sharedIndex[c]!;
    // The bolts in whose spawn disc both this enemy and the aimed one stand: there it is the ids that order them.
    const tie = cellSpawn & tSpawn;
    // P(an enemy here is hit) for this rank `u`; `ties` are the bolts whose spawn disc the aimed enemy shares and ranks ahead in, `aimed` whether it is there at all.
    const hitHere = (u: number, aimed: boolean, ties: number): number => {
      let alive = 0;
      for (let i = 0; i < bolts; i += 1) {
        const bit = 1 << i;
        if ((mask & bit) === 0) continue;
        const takenByAimed = aimed && tIn[i]! && (tKey[i]! < keys[i * count + c]! || (ties & bit) !== 0);
        if (takenByAimed && cap === 1) continue;
        alive |= bit;
        limit[i] = takenByAimed ? cap - 1 : cap;
        // The cells at this one distance (itself among them) are half before it; a spawn disc's are before it by id.
        const low = prefix[i]![rankLow[i * count + c]!]!;
        meanBefore[i] = (cellSpawn & bit) !== 0 ? u * spawnMass[i]! : low + (prefix[i]![rankHigh[i * count + c]!]! - low) / 2;
      }
      return hitAt(alive);
    };
    const atRank = (u: number): { readonly free: number; readonly aimed: number } => {
      if (a >= 0) gather(u, cellSpawn, a * shared.length, before, tied);
      return {
        free: hitHere(u, false, 0),
        aimed: tie === 0 ? hitHere(u, true, 0) : u * hitHere(u, true, tie) + (1 - u) * hitHere(u, true, 0),
      };
    };
    if (cellSpawn === 0) {
      const here = atRank(0.5);
      without += m * here.free;
      withTarget += m * here.aimed;
    } else {
      for (const node of RANK_NODES) {
        const here = atRank(node.u);
        without += m * node.weight * here.free;
        withTarget += m * node.weight * here.aimed;
      }
    }
  }
  return { inside, distinct: hitTarget + withTarget, distinctWithoutPrimary: without };
}

const MAX_RESULTS = 512;
const results = new Map<string, DistinctHits>();

/**
 * Expected distinct enemies the `corridors` reach over the cast field's distance law, with the aimed
 * enemy and without. Callers gate on `projectileUnionRefusal` first. Throws `ProjectileUnionTooLarge`
 * where no lattice within the budget keeps the cells at `CELL_MASS_BOUND`. `stepM` fixes the cell side
 * (no budget, no refusal): the lattice a price is checked against is the same corridors at half its step.
 */
export function distinctProjectileHits(
  corridors: readonly Corridor[],
  cap: number,
  frame: FieldRegion['frame'],
  reachM: number,
  entry: boolean,
  field: CastFieldEvidence,
  density: number,
  stepM?: number,
): DistinctHits {
  const key = `${stepM}|${frame}|${JSON.stringify(corridors)}|${cap}|${reachM}|${entry}|${density}|${JSON.stringify(field)}`;
  const known = results.get(key);
  if (known !== undefined) return known;
  const geo = geometryOf(corridors, Math.sqrt(CELL_MASS_BOUND / othersDensityScale(field, density)), stepM);
  let primaryShare = 0;
  let distinct = 0;
  let distinctWithoutPrimary = 0;
  for (const node of castDistances(reachM, field, entry)) {
    const counts = nodeCounts(geo, frame, node.distanceM, cap, field, density);
    if (counts.inside) primaryShare += node.weight;
    distinct += node.weight * counts.distinct;
    distinctWithoutPrimary += node.weight * counts.distinctWithoutPrimary;
  }
  const hits = { primaryShare, distinct, distinctWithoutPrimary };
  if (results.size >= MAX_RESULTS) results.clear();
  results.set(key, hits);
  return hits;
}
